import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Timeout di default per richieste rapide (GET/PUT leggere).
const DEFAULT_TIMEOUT = 30000; // 30s
// Timeout esteso per operazioni lunghe che innescano la classificazione LLM
// (sync banca/PayPal, run dell'agente, import). Evita falsi errori di timeout.
export const LONG = { timeout: 120000 }; // 120s

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: DEFAULT_TIMEOUT,
});

// ---------------------------------------------------------------- Auth token (Bearer)
// NOTA (sicurezza): il token è salvato in localStorage PER SCELTA, perché serve come
// header `Authorization: Bearer` nell'app nativa Capacitor (le WebView native non
// gestiscono in modo affidabile i cookie httpOnly cross-site). Sul web il backend
// imposta COMUNQUE anche un cookie `session_token` httpOnly+Secure+SameSite=None come
// difesa aggiuntiva. La superficie XSS è mitigata da React (escaping di default) e
// dall'assenza di `dangerouslySetInnerHTML`. Il token è opaco e revocabile lato server.
const TOKEN_KEY = "liqo_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    // Sessione scaduta/non valida: pulisci e notifica (ignora le rotte /auth/* in fase di login)
    if (err?.response?.status === 401 && !url.includes("/auth/")) {
      setToken(null);
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(err);
  }
);

// ---------------------------------------------------------------- Auth API
export const authRegister = (email, password, name) =>
  api.post("/auth/register", { email, password, name }).then((r) => r.data);
export const authLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const authGoogleSession = (session_id) =>
  api.post("/auth/google/session", { session_id }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const deleteAccount = () => api.delete("/auth/account").then((r) => r.data);

export const getProfile = () => api.get("/profile").then((r) => r.data);
export const submitOnboarding = (data) => api.post("/onboarding", data).then((r) => r.data);
export const getBalance = () => api.get("/balance").then((r) => r.data);
export const getCards = () => api.get("/cards").then((r) => r.data);
export const getCardDetail = (id) => api.get(`/cards/${id}`).then((r) => r.data);
export const dismissCard = (id) => api.post(`/cards/${id}/dismiss`).then((r) => r.data);
export const actionCard = (id, label) => api.post(`/cards/${id}/action`, { label }).then((r) => r.data);
export const getTransactions = () => api.get("/transactions").then((r) => r.data);
export const getExcludedTransfers = () => api.get("/transactions/excluded").then((r) => r.data);
export const updateCategory = (id, category) =>
  api.put(`/transactions/${id}/category`, { category }).then((r) => r.data);
export const setTransfer = (id, isTransfer) =>
  api.post(`/transactions/${id}/transfer`, { is_transfer: isTransfer }).then((r) => r.data);
export const setTransactionScope = (id, scope) =>
  api.post(`/transactions/${id}/scope`, { scope }).then((r) => r.data);

// Movimenti in CONTANTI inseriti manualmente (spese/entrate non tracciate dalla banca).
export const addManualTransaction = (payload) =>
  api.post("/transactions/manual", payload).then((r) => r.data);
export const deleteTransaction = (id) =>
  api.delete(`/transactions/${id}`).then((r) => r.data);
export const runAgent = () => api.post("/agent/run", null, LONG).then((r) => r.data);
export const sendChat = (message) => api.post("/chat", { message }).then((r) => r.data);
export const getChatHistory = () => api.get("/chat/history").then((r) => r.data);
export const getCategories = () => api.get("/categories").then((r) => r.data);

// ---------------------------------------------------------------- Investment Coach
export const getInvestCoach = () => api.get("/invest/coach").then((r) => r.data);
export const getPortfolioIdeas = (refresh = false) =>
  api.get("/portfolio/ideas", { params: refresh ? { refresh: 1 } : {}, ...LONG }).then((r) => r.data);
export const updateRiskProfile = (risk_profile) =>
  api.put("/profile/risk", { risk_profile }, LONG).then((r) => r.data);

// ---------------------------------------------------------------- Report mensile / Andamento
export const getMonthlyReport = () => api.get("/monthly/report").then((r) => r.data);
export const getMonthlyHistory = (months = 6) =>
  api.get("/monthly/history", { params: { months } }).then((r) => r.data);
export const getMonthlyCategoryTransactions = (month, category, kind) =>
  api
    .get("/monthly/category-transactions", { params: { month, category, ...(kind ? { kind } : {}) } })
    .then((r) => r.data);
export const getMonthPace = () => api.get("/month/pace").then((r) => r.data);
// Vista "Questo mese" (mese corrente): entrate/uscite + ripartizione per categoria + ritmo.
export const getMonthSummary = () => api.get("/month/summary").then((r) => r.data);
export const getPortfolioSignals = () => api.get("/portfolio/signals", LONG).then((r) => r.data);
export const getPortfolioHistory = (days = 90) =>
  api.get("/portfolio/history", { params: { days } }).then((r) => r.data);

// ---------------------------------------------------------------- Open Banking (Enable Banking)
export const getBankStatus = () => api.get("/bank/status").then((r) => r.data);
export const getBankBalances = () => api.get("/bank/balances").then((r) => r.data);
export const setAccountVisibility = (key, hidden) =>
  api.post("/bank/accounts/visibility", { key, hidden }).then((r) => r.data);
export const getAnalytics = (days = 30) =>
  api.get("/analytics", { params: { days } }).then((r) => r.data);

// ---------------------------------------------------------------- Fisco / Tasse
export const getTax = () => api.get("/tax").then((r) => r.data);
export const getTaxConfig = () => api.get("/tax/config").then((r) => r.data);
export const getTaxMeta = () => api.get("/tax/meta").then((r) => r.data);
export const searchAteco = (q = "") =>
  api.get("/tax/ateco/search", { params: { q } }).then((r) => r.data.results);
export const updateTaxConfig = (payload) => api.put("/tax/config", payload, LONG).then((r) => r.data);

// ---------------------------------------------------------------- Abbonamenti
export const getSubscriptions = () => api.get("/subscriptions").then((r) => r.data);

// ---------------------------------------------------------------- Spese fisse manuali
export const getFixedExpenses = () => api.get("/fixed-expenses").then((r) => r.data);
export const addFixedExpense = (payload) => api.post("/fixed-expenses", payload).then((r) => r.data);
export const updateFixedExpense = (id, payload) =>
  api.put(`/fixed-expenses/${id}`, payload).then((r) => r.data);
export const deleteFixedExpense = (id) => api.delete(`/fixed-expenses/${id}`).then((r) => r.data);

// ---------------------------------------------------------------- Notifiche
export const getNotifications = () => api.get("/notifications").then((r) => r.data);
export const markNotificationsRead = (payload = { all: true }) =>
  api.post("/notifications/read", payload).then((r) => r.data);

// ---------------------------------------------------------------- Push (FCM native)
export const registerPushToken = (token, platform) =>
  api.post("/push/register", { token, platform }).then((r) => r.data);
export const unregisterPushToken = (token) =>
  api.post("/push/unregister", { token }).then((r) => r.data);
export const getPushStatus = () => api.get("/push/status").then((r) => r.data);
export const sendPushTest = () => api.post("/push/test").then((r) => r.data);

// ---------------------------------------------------------------- Abbonamento (Trial + Pro)
export const getSubscription = () => api.get("/subscription").then((r) => r.data);
export const subscribe = (plan) => api.post("/subscription/subscribe", { plan }).then((r) => r.data);
export const cancelSubscription = () => api.post("/subscription/cancel").then((r) => r.data);

// ---------------------------------------------------------------- PayPal
export const getPaypalStatus = () => api.get("/paypal/status").then((r) => r.data);
export const paypalSync = () => api.post("/paypal/sync", null, LONG).then((r) => r.data);
export const paypalDevSeed = () => api.post("/paypal/dev/seed", null, LONG).then((r) => r.data);
export const paypalDisconnect = () => api.post("/paypal/disconnect").then((r) => r.data);

// ---------------------------------------------------------------- Investimenti / Patrimonio
export const getPortfolio = () => api.get("/portfolio").then((r) => r.data);
export const getPortfolioSummary = () => api.get("/portfolio/summary").then((r) => r.data);
export const searchInstruments = (q, type) =>
  api.get("/portfolio/search", { params: { q, type } }).then((r) => r.data.results);
export const getFxRate = (currency) =>
  api.get("/portfolio/fx", { params: { currency } }).then((r) => r.data);
export const addHolding = (payload) => api.post("/portfolio/holdings", payload, LONG).then((r) => r.data);
export const updateHolding = (id, payload) => api.put(`/portfolio/holdings/${id}`, payload, LONG).then((r) => r.data);
export const deleteHolding = (id) => api.delete(`/portfolio/holdings/${id}`).then((r) => r.data);
// Azzera l'intero portafoglio (rimuove tutti gli asset demo/di test).
export const portfolioReset = () => api.post("/portfolio/reset", null, LONG).then((r) => r.data);
export const getBankInstitutions = (country = "IT") =>
  api.get("/bank/institutions", { params: { country } }).then((r) => r.data);
export const bankConnect = (payload) => api.post("/bank/connect", payload).then((r) => r.data);
export const bankComplete = (payload) =>
  api.post("/bank/complete", payload, LONG).then((r) => r.data);
export const bankSync = () => api.post("/bank/sync", null, LONG).then((r) => r.data);
export const bankDisconnect = (connection_id) =>
  api.post("/bank/disconnect", connection_id ? { connection_id } : {}).then((r) => r.data);
// Rimuove i dati DEMO/mock erroneamente seminati in produzione (NON tocca i conti reali).
export const bankResetDemo = () => api.post("/bank/reset-demo", null, LONG).then((r) => r.data);

export const importRevolutCsv = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/import/revolut", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};
